import { AxiosResponse } from 'axios';
import jwt_decode from 'jwt-decode';
import { createContext, useContext, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { GetUserOrganization } from 'services/auth-service';
import { updatePrivateAreaLanguage } from 'services/private-area-services';
import { axiosInstance } from 'utils/AxiosInstance';
import { API_PATHS, USER_ROLES } from 'utils/constants';
import {
  Auth,
  IOrganizationResponse,
  IUserLoginRequest,
  IUserLoginResponse,
  User,
} from 'utils/contracts';
import { calcTotalAccountPoints, RemoveAllLoggedInUsersData } from 'utils/utils';
import { resetWhatsNewForLogin } from 'utils/whatsNewSeen';
import LanguageContext from './LanguageContext';

interface IAuthContext {
  isAuthenticated?: boolean;
  user?: User;
  role?: string;
  auth?: Auth;
  organizationId?: string;
  setOrganizationId: (orgId: string) => void;
  organizations?: IOrganizationResponse;
  isInspectionOrganization?: boolean;
  login: (data: IUserLoginRequest) => Promise<IUserLoginResponse>;
  logout: (path?: any) => void;
  getOrganization: (orgId: any) => Promise<void>;
  setOrganization: (org: any) => void;
  adminLogin: (data: any) => Promise<void>;
  adminLogout: (path?: any) => void;
  loginAfterInvitationProcess: (jwtData: any) => Promise<void>;
  updateUser: (userData: User) => void;
  setRole: (role: string) => void;
}

interface IJWTDecode {
  exp: number;
  iat: number;
  id: string;
  name: string;
  authorization: string;
  organizationId: string;
  email: string;
  phone: string;
  address: string;
  language: string;
}

const UseAuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider = ({ children }: any) => {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const { language, setLanguage } = useContext(LanguageContext)
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem('token') !== null
  );
  const [role, setRole] = useState(() => {
    const role = localStorage.getItem('role') ?? null;
    return role;
  });

  const [organizationId, setOrganizationId] = useState(() => {
    const Id = localStorage.getItem('organizationId') ?? null;
    return Id;
  });
  const [auth, setauth] = useState(() => {
    const auth = localStorage.getItem('auth');
    return auth ? JSON.parse(auth) : null;
  });
  const [user, setuser] = useState(() => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  });
  const [organizations, setOrganizations] = useState(() => {
    const organizations = localStorage.getItem('organizations');
    return organizations ? JSON.parse(organizations) : null;
  });
  const [isInspectionOrganization, setIsInspectionOrganization] = useState(() => {
    const org = localStorage.getItem('organizations');
    return org ? JSON.parse(org)?.isInspectionType || false : false;
  });
  const navigate = useNavigate();


  const login = async (data: IUserLoginRequest): Promise<IUserLoginResponse> => {
    const res = await loginMutation.mutateAsync(data);
    const responseData = res.data;
    const decoded: IJWTDecode = jwt_decode<any>(responseData.token);
    const calculateduser: User = {
      id: decoded.id,
      name: decoded.name,
      organizationId: decoded.organizationId,
      role: decoded.authorization,
      email: decoded.email,
      phone: decoded.phone,
    };
    return {
      token: responseData.token,
      refreshToken: responseData.refreshToken,
      user: calculateduser
    } as IUserLoginResponse;
  };

  const adminLogin = async (data: any) => {
    await adminLoginMutation.mutateAsync(data);
  };

  const getOrganization = async (orgId: any) => {
    await organizationMutation.mutateAsync(orgId);
  };

  const setOrganization = async (org: any) => {
    if (org) {
      // Short/list org payloads often omit totalCredits even when transactions exist.
      // Unlock/top-up UI gates on organizations.totalCredits — keep it populated.
      const next = { ...org };
      if (next.totalCredits == null && next.transactions) {
        next.totalCredits = calcTotalAccountPoints(next);
      }
      setOrganizations(next);
      setIsInspectionOrganization(next.isInspectionType || false);
      try {
        const { transactions, ...rest } = next;
        localStorage.setItem(
          'organizations',
          JSON.stringify(
            transactions
              ? { ...rest, totalCredits: next.totalCredits ?? 0 }
              : next
          )
        );
      } catch {
        // QuotaExceeded or similar — in-memory org still has credits for the session.
      }
    }
  };

  const adminLoginMutation = useMutation({
    mutationFn: async (data: any) => {
      const loginResponse = await axiosInstance.post<any, AxiosResponse<any>>(
        API_PATHS.ADMIN_LOGIN,
        JSON.stringify({
          password: data.password,
        })
      );
      return loginResponse;
    },
    async onSuccess(data) {
      const responseData = data.data;
      const decoded: IJWTDecode = jwt_decode<any>(responseData.token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        logout();
      }
      const auth = {
        exp: decoded.exp,
        id: USER_ROLES.ADMIN,
        token: responseData.token,
      };
      const calculateduser = {
        role: USER_ROLES.ADMIN,
      };

      localStorage.setItem('auth', JSON.stringify(auth));
      localStorage.setItem('user', JSON.stringify(calculateduser));
      localStorage.setItem('token', responseData.token);
      localStorage.setItem('role', USER_ROLES.ADMIN);
      setuser(calculateduser);
      setIsAuthenticated(responseData?.token !== null);
      setauth(auth);
      setRole(USER_ROLES.ADMIN);
      navigate('/admin/dashboard', { replace: true });
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: IUserLoginRequest) => {
      const loginResponse = await axiosInstance.post<
        any,
        AxiosResponse<IUserLoginResponse>
      >(
        API_PATHS.NORMAL_USER_LOGIN,
        JSON.stringify({
          email: data.email,
          password: data.password,
        })
      );
      return loginResponse;
    },
    async onSuccess(data) {

      const responseData = data.data;
      const decoded: IJWTDecode = jwt_decode<any>(responseData.token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp < currentTime) {
        logout();
        return null;
      }

      const auth = {
        exp: decoded.exp,
        id: decoded.id,
        token: responseData.token,
        refreshToken: responseData.refreshToken
      };
      const calculateduser = {
        id: decoded.id,
        name: decoded.name,
        organizationId: decoded.organizationId,
        role: decoded.authorization,
        email: decoded.email,
        phone: decoded.phone,
        address: decoded.address,
        language: decoded.language
      };

      localStorage.setItem('auth', JSON.stringify(auth));
      localStorage.setItem('user', JSON.stringify(calculateduser));
      localStorage.setItem('token', responseData.token);
      localStorage.setItem('refreshToken', responseData.refreshToken);
      localStorage.setItem('role', decoded.authorization);
      localStorage.setItem('organizationId', decoded.organizationId);
      resetWhatsNewForLogin(decoded.id);

      setuser(calculateduser);
      setIsAuthenticated(true);
      setauth(auth);
      setRole(decoded.authorization);
      setOrganizationId(decoded.organizationId);

      if (language !== decoded.language) {
        await updatePrivateAreaLanguage({ language: language });
      }

      await organizationMutation.mutateAsync(decoded.organizationId);
      return {
        token: responseData.token,
        refreshToken: responseData.refreshToken,
        user: calculateduser
      };
    },
    onError: (error) => {
      console.error('error login :', error);
    }
  });

  const loginAfterInvitationProcess = async (jwtData: any) => {
    const responseData = jwtData;
    const decoded: IJWTDecode = jwt_decode<any>(responseData.token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      logout();
    }
    const auth = {
      exp: decoded.exp,
      id: decoded.id,
      token: responseData.token,
    };
    const calculateduser = {
      id: decoded.id,
      name: decoded.name,
      organizationId: decoded.organizationId,
      role: decoded.authorization,
      email: decoded.email,
      phone: decoded.phone,
      address: decoded.address,
      language: decoded.language
    };

    localStorage.setItem('auth', JSON.stringify(auth));
    localStorage.setItem('user', JSON.stringify(calculateduser));
    localStorage.setItem('token', responseData.token);
    localStorage.setItem('refreshToken', responseData.refreshToken);
    localStorage.setItem('role', decoded.authorization);
    localStorage.setItem('organizationId', decoded.organizationId);
    resetWhatsNewForLogin(decoded.id);

    setuser(calculateduser);
    setIsAuthenticated(responseData?.token !== null);
    setauth(auth);
    setRole(decoded.authorization);
    setOrganizationId(decoded.organizationId);
    await organizationMutation.mutateAsync(decoded.organizationId);
    
    // Redirect inspectors and inspector managers to inspections page
    const redirectPath = (decoded.authorization === USER_ROLES.INSPECTOR || decoded.authorization === USER_ROLES.INSPECTOR_MANAGER)
      ? '/inspections'
      : '/dashboard';
    navigate(redirectPath, { replace: true });
    
  };

  const organizationMutation = useMutation({
    mutationFn: async (orgId: any) => {
      return await GetUserOrganization(orgId);
    },
    onSuccess(data) {
      const { transactions, ...rest } = data;
      setOrganizations(rest);
      localStorage.setItem('organizations', JSON.stringify(rest));
    },
  });

  const adminLogout = (path: any = '') => {
    RemoveAllLoggedInUsersData();
    const redirectPath = path ? `?redirect=${path}` : '';
    setuser(null);
    setIsAuthenticated(false);
    setauth(null);
    setRole(null);
    navigate(`admin/login${redirectPath}`, { replace: true });
  };

  const logout = (path: any = '') => {
    RemoveAllLoggedInUsersData();
    const redirectPath = path ? `?redirect=${path}` : '';
    setOrganizations(null);
    setOrganizationId(null);
    setuser(null);
    setIsAuthenticated(false);
    setauth(null);
    setRole(null);
    navigate(`/login${redirectPath}`, { replace: true });
  };

  const updateUser = (userData: User) => {
    setuser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <UseAuthContext.Provider
      value={
        {
          login,
          logout,
          isAuthenticated,
          user,
          organizations,
          auth,
          role,
          setRole: (r: string) => setRole(r),
          organizationId,
          setOrganizationId: (orgId: string) => {
            setOrganizationId(orgId);
            if (orgId) {
              localStorage.setItem('organizationId', orgId);
            } else {
              localStorage.removeItem('organizationId');
            }
          },
          isInspectionOrganization,
          getOrganization,
          setOrganization,
          adminLogin,
          adminLogout,
          loginAfterInvitationProcess,
          updateUser,
        } as IAuthContext
      }
      key={'LOGINCONTEXT'}
    >
      {children}
    </UseAuthContext.Provider>
  );
};

export default UseAuthContext;
